/**
 * cam-audit-report.ts — downloadable audit report for CAM Audit V2
 * (12-point LOS vs CAM comparison).
 *
 * Kept in lock-step with the on-screen grid: imports the SAME row list, state
 * derivation, and score computation from CamComparisonGrid (deriveRow /
 * computeScore / CAM_CHECKLIST_ROWS) so the report and the web view never
 * disagree on statuses or score.
 *
 * Self-contained, print-ready HTML with a flat Material-3 look matching the
 * app (Axis burgundy, Lato). Structure: header, loan summary + score card,
 * concise audit summary table, detailed side-by-side only for issues, and an
 * optional banking section.
 */

import type {
  FindingDTO, LosApplicationDataDTO, LoanFileParsingView,
  BankingVerificationDTO,
} from '@loanaudit/shared';
import { REPORT_JS } from './audit-report';
import { toast } from '@/acc-ui/primitives/Toast';
import {
  CAM_CHECKLIST_ROWS, deriveRow, computeScore,
  type DerivedRow, type RowState,
} from './CamComparisonGrid';

// =========================================================================
// Public types
// =========================================================================

interface ReportLoanFile {
  id: string;
  loanNumber: string;
  borrowerName: string;
  productCode: string;
  branchCode: string;
  state: string;
}

export interface GenerateCamReportInput {
  loanFile: ReportLoanFile;
  findings: FindingDTO[];
  losData: LosApplicationDataDTO | null;
  parsingView: LoanFileParsingView | null;
  documents: Array<{ id: string; originalFileName: string; documentType: string }>;
  validatedAt?: string | null;
  bankingVerification?: BankingVerificationDTO | null;
}

// =========================================================================
// Helpers
// =========================================================================

function escapeHtml(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function fmtInr(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return `₹${n.toLocaleString('en-IN')}`;
}

function fmtPct(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return `${n}%`;
}

function getCamFields(
  parsingView: LoanFileParsingView | null,
): Record<string, { value: unknown; confidence: number }> | null {
  const docs = parsingView?.documents ?? [];
  const cam = docs.find((d) => d.classification?.predictedType === 'cam');
  return (cam?.extraction?.fields as Record<string, { value: unknown; confidence: number }> | undefined) ?? null;
}

function camField<T = unknown>(
  fields: Record<string, { value: unknown }> | null,
  name: string,
): T | null {
  if (!fields) return null;
  return (fields[name]?.value ?? null) as T | null;
}

// =========================================================================
// Public entry point
// =========================================================================

export function generateAndOpenCamReport(input: GenerateCamReportInput): void {
  let html: string;
  try {
    html = buildReportHtml(input);
  } catch (err) {
    console.error('Audit report build failed:', err);
    const errMsg = err instanceof Error ? err.message : String(err);
    toast.error(`Could not generate the audit report: ${errMsg}. Try Re-run parsing / Re-run Audit, then download again.`);
    return;
  }

  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  let openedTab: Window | null = null;
  try {
    openedTab = window.open(url, '_blank', 'noopener');
  } catch {
    openedTab = null;
  }
  if (!openedTab || openedTab.closed) {
    const a = document.createElement('a');
    a.href = url;
    a.download = `loan-audit-report-${input.loanFile.loanNumber}.html`;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// =========================================================================
// Stylesheet (self-contained, flat Material-3, Axis burgundy + Lato)
// =========================================================================

const REPORT_CSS = `
:root{
  --primary:#61002c; --primary-container:#841542; --on-primary:#ffffff;
  --bg:#f8f9fa; --surface:#ffffff; --surface-low:#f3f4f5; --surface-high:#e7e8e9;
  --surface-variant:#e1e3e4; --on-surface:#191c1d; --on-surface-variant:#564146;
  --outline:#897176; --outline-variant:#dcbfc5;
  --error:#ba1a1a; --error-container:#ffdad6; --on-error:#ffffff; --on-error-container:#93000a;
  --amber:#b45309; --green:#047857;
}
*{box-sizing:border-box}
html{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale}
body{margin:0;background:var(--bg);color:var(--on-surface);
  font-family:Lato,'Segoe UI',system-ui,-apple-system,sans-serif;
  font-size:14px;line-height:1.5;padding:24px 16px;display:flex;flex-direction:column;align-items:center}
.mono{font-family:'JetBrains Mono',ui-monospace,SFMono-Regular,Menlo,Consolas,monospace}
code{font-family:'JetBrains Mono',ui-monospace,monospace;font-size:12px;background:var(--surface-low);padding:1px 5px;border-radius:3px}
.dim{color:var(--on-surface-variant);opacity:.75}

.toolbar{width:100%;max-width:880px;display:flex;justify-content:flex-end;gap:8px;margin-bottom:14px}
.btn{font-family:inherit;font-size:13px;padding:7px 14px;border-radius:8px;border:1px solid var(--outline-variant);background:var(--surface);color:var(--on-surface);cursor:pointer}
.btn-primary{background:var(--primary);color:var(--on-primary);border-color:var(--primary)}
.btn:hover{filter:brightness(0.97)}

.page{background:var(--surface);width:100%;max-width:880px;padding:32px;border:1px solid var(--outline-variant)}

.head{border-bottom:3px solid var(--primary);padding-bottom:16px;margin-bottom:24px}
.eyebrow{color:var(--primary);font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:.14em;margin-bottom:4px}
.title{font-size:28px;font-weight:800;color:var(--primary);margin:0 0 4px;letter-spacing:-.01em}
.subid{font-size:12px;color:var(--on-surface-variant)}

.grid{display:grid;grid-template-columns:7fr 5fr;gap:24px;margin-bottom:8px}
@media(max-width:640px){.grid{grid-template-columns:1fr}}
.sub-h{font-size:13px;font-weight:700;color:var(--on-surface);text-transform:uppercase;letter-spacing:.04em;border-bottom:1px solid var(--outline-variant);padding-bottom:5px;margin:0 0 8px}
table.kv{width:100%;border-collapse:collapse}
table.kv tr{border-bottom:1px solid var(--surface-variant)}
table.kv tr:last-child{border-bottom:none}
table.kv th{text-align:left;font-weight:400;color:var(--on-surface-variant);font-size:12px;padding:5px 16px 5px 0;width:42%;vertical-align:top}
table.kv td{padding:5px 0;font-weight:600;color:var(--on-surface)}

.scorecard{display:flex;flex-direction:column;align-items:center;justify-content:center;background:var(--bg);border:1px solid var(--outline-variant);border-radius:8px;padding:16px}
.ring{width:104px;height:104px;border-radius:50%;border:6px solid var(--outline);color:var(--outline);display:flex;flex-direction:column;align-items:center;justify-content:center;margin-bottom:10px}
.ring.green{border-color:var(--green);color:var(--green)}
.ring.amber{border-color:var(--amber);color:var(--amber)}
.ring.red{border-color:var(--error);color:var(--error)}
.ring .num{font-size:30px;font-weight:800;line-height:1}
.ring .den{font-size:11px;opacity:.8;margin-top:3px}
.verdict{width:100%;padding:8px 10px;border-radius:4px;text-align:center;margin-bottom:10px;font-size:12px;font-weight:600}
.verdict.red{background:var(--error-container);color:var(--on-error-container);border-left:4px solid var(--error)}
.verdict.amber{background:#fff4e5;color:#92400e;border-left:4px solid var(--amber)}
.verdict.green{background:#dcfce7;color:#065f46;border-left:4px solid var(--green)}
.chips{display:flex;flex-wrap:wrap;justify-content:center;gap:6px}
.chip{background:var(--surface);border:1px solid var(--outline-variant);border-radius:999px;padding:4px 10px;font-size:12px;color:var(--on-surface-variant)}
.chip b{color:var(--on-surface)} .chip b.err{color:var(--error)} .chip b.out{color:var(--outline)}

.block{margin-top:32px;padding-top:24px;border-top:1px solid var(--outline-variant)}
.section-h{font-size:20px;font-weight:700;color:var(--primary);margin:0 0 16px}

table.summary{width:100%;border-collapse:collapse}
table.summary thead{background:var(--primary-container);color:var(--on-primary)}
table.summary thead th{padding:9px 8px;text-align:left;font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:.02em}
table.summary tbody td{padding:9px 8px;border-bottom:1px solid var(--surface-variant);font-size:13px;vertical-align:top}
table.summary .c-pt{text-align:center;width:40px;font-weight:700;color:var(--on-surface-variant)}
table.summary .c-status{width:104px} table.summary .c-sev{width:80px}
.row-alt{background:rgba(243,244,245,.55)}
.row-fail{background:rgba(255,218,214,.22)}
.check-name{font-weight:700;color:var(--on-surface)}
.check-desc{color:var(--on-surface-variant);font-size:11px;margin-top:2px}
.sev{font-weight:700;text-transform:uppercase;font-size:11px;color:var(--on-surface-variant)}
.sev.high,.sev.critical{color:var(--error)}
.concl-pass{color:var(--on-surface-variant)}
.concl-fail{color:var(--error);font-weight:600}
.concl-warn{color:var(--amber)}
.concl-na{color:var(--on-surface-variant);opacity:.75}

.pill{display:inline-block;padding:2px 9px;border-radius:999px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.02em}
.pill.matched{background:var(--surface-high);color:var(--on-surface)}
.pill.fail{background:var(--error);color:var(--on-error)}
.pill.na{background:var(--surface-variant);color:var(--on-surface-variant)}
.pill.warn{background:#fff4e5;color:#92400e}

.issue{border:1px solid var(--outline-variant);border-left:4px solid var(--error);border-radius:6px;overflow:hidden;background:var(--surface);margin-bottom:14px}
.issue.amber{border-left-color:var(--amber)}
.issue-head{background:rgba(255,218,214,.22);padding:9px 10px;border-bottom:1px solid var(--outline-variant);display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.issue.amber .issue-head{background:#fff4e5}
.issue-point{font-size:11px;font-weight:700;text-transform:uppercase;color:var(--primary);border:1px solid var(--primary);background:var(--surface);padding:2px 8px;border-radius:3px}
.issue-title{font-size:16px;font-weight:600;color:var(--on-surface);flex:1}
.issue-body{padding:10px 12px;font-size:14px;color:var(--on-surface)}
.issue-src{padding:8px 12px;border-top:1px dashed var(--outline-variant);background:var(--bg);font-size:13px;color:var(--on-surface-variant)}

table.compare{width:100%;border-collapse:collapse;margin:6px 0 4px}
table.compare th{background:var(--surface-low);text-align:left;font-size:11px;text-transform:uppercase;color:var(--on-surface-variant);padding:6px 10px;border-bottom:1px solid var(--outline-variant)}
table.compare td{padding:6px 10px;border-bottom:1px solid var(--surface-variant);font-size:13px}
table.compare td.num{font-variant-numeric:tabular-nums}
.v-ok{color:var(--green);font-weight:700} .v-no{color:var(--error);font-weight:700}
.cond-cols{display:grid;grid-template-columns:1fr 1fr;gap:16px;padding:10px 12px}
.cond-cols h4{margin:0 0 6px;font-size:12px;text-transform:uppercase;color:var(--on-surface-variant)}
.cond-cols ol{margin:0;padding-left:18px} .cond-cols li{margin:2px 0;font-size:13px}

.ok-msg{color:var(--green);font-weight:600}

.footer{margin-top:32px;padding-top:20px;border-top:2px solid var(--primary-container);text-align:center;font-size:13px;color:var(--on-surface-variant)}
.footer .sign{display:flex;justify-content:space-around;gap:24px;margin-top:22px;padding-top:14px}
.footer .sign div{font-size:13px;color:var(--on-surface)}

@media print{
  .no-print{display:none!important}
  body{padding:0;background:#fff}
  .page{border:none;max-width:none;padding:0}
  .block{break-inside:avoid}
  .issue{break-inside:avoid}
}
@page{size:A4;margin:14mm}
`;

// =========================================================================
// HTML builder
// =========================================================================

function buildReportHtml(input: GenerateCamReportInput): string {
  const { loanFile, findings, losData, parsingView, bankingVerification } = input;

  const findingsByRuleId = new Map<string, FindingDTO>();
  for (const f of findings) {
    if (!findingsByRuleId.has(f.ruleId)) findingsByRuleId.set(f.ruleId, f);
  }
  const camFields = getCamFields(parsingView);
  const hasCamData = camFields !== null;
  const hasLosData = losData !== null;

  // SAME derivation + score as the on-screen grid.
  const rows = CAM_CHECKLIST_ROWS.map(
    (r) => deriveRow(r, findingsByRuleId, hasCamData, hasLosData, loanFile.productCode),
  );
  const stats = computeScore(rows);

  const tier: 'green' | 'amber' | 'red' =
    stats.mismatch > 0
      ? (stats.score >= 70 ? 'amber' : 'red')
      : (stats.score >= 90 ? 'green' : stats.score >= 70 ? 'amber' : 'red');

  const verdict =
    stats.mismatch === 0 && stats.inconclusive === 0
      ? 'CAM and LOS aligned, eligible for disbursement'
      : stats.mismatch > 0
        ? 'Mismatches found, remediation or waiver required'
        : 'Inconclusive, CAM and/or LOS data missing';
  const verdictTone: 'green' | 'amber' | 'red' =
    stats.mismatch > 0 ? 'red' : (stats.inconclusive > 0 ? 'amber' : 'green');

  const generatedAtStr = new Date().toLocaleString('en-IN', { dateStyle: 'long', timeStyle: 'short' });

  const losLd = losData?.data?.loanDetails;
  const sanctionDate = losData?.data?.dates?.sanctionDate ?? '—';
  const disbDate = losData?.data?.dates?.disbursementDate ?? '—';

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>CAM-vs-LOS Audit · ${escapeHtml(loanFile.loanNumber)}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Lato:wght@400;700;900&family=JetBrains+Mono:wght@400;700&display=swap" rel="stylesheet">
<style>${REPORT_CSS}</style>
</head>
<body>
<div class="toolbar no-print">
  <button type="button" id="btn-print" class="btn btn-primary">Print / Save as PDF</button>
  <button type="button" id="btn-download-html" class="btn btn-secondary">Download HTML</button>
  <button type="button" id="btn-close" class="btn btn-ghost">Close</button>
</div>

<div class="page">
  <header class="head">
    <div class="eyebrow">Agentic Loan Audit</div>
    <h1 class="title">CAM-vs-LOS Compliance Audit</h1>
    <div class="subid mono">${escapeHtml(loanFile.loanNumber)} · ${escapeHtml(loanFile.borrowerName)}</div>
  </header>

  <div class="grid">
    <div>
      <h3 class="sub-h">Loan Summary</h3>
      <table class="kv">
        <tbody>
          <tr><th>Product</th><td>${escapeHtml(loanFile.productCode)}</td></tr>
          <tr><th>Branch</th><td>${escapeHtml(loanFile.branchCode)}</td></tr>
          <tr><th>Sanctioned</th><td>${fmtInr(losLd?.sanctionedAmount)}</td></tr>
          <tr><th>Interest rate</th><td>${fmtPct(losLd?.interestRatePct)}</td></tr>
          <tr><th>Tenure</th><td>${losLd?.tenureMonths ?? '—'} months</td></tr>
          <tr><th>EMI</th><td>${fmtInr(losLd?.emiAmount)}</td></tr>
          <tr><th>Sanction date</th><td>${escapeHtml(sanctionDate)}</td></tr>
          <tr><th>Disbursement</th><td>${escapeHtml(disbDate)}</td></tr>
        </tbody>
      </table>
    </div>

    <div class="scorecard">
      <div class="ring ${tier}">
        <div class="num">${stats.score}</div>
        <div class="den">/ 100</div>
      </div>
      <div class="verdict ${verdictTone}">${escapeHtml(verdict)}</div>
      <div class="chips">
        <div class="chip"><b>${stats.matched}</b> matched</div>
        ${stats.deviation > 0 ? `<div class="chip"><b>${stats.deviation}</b> deviation OK</div>` : ''}
        <div class="chip"><b class="err">${stats.mismatch}</b> mismatch${stats.mismatch === 1 ? '' : 'es'}</div>
        ${stats.inconclusive > 0 ? `<div class="chip"><b>${stats.inconclusive}</b> inconclusive</div>` : ''}
        ${stats.skipped > 0 ? `<div class="chip"><b class="out">${stats.skipped}</b> N/A</div>` : ''}
      </div>
    </div>
  </div>

  ${renderSummaryTable(rows)}
  ${renderIssuesDetail(rows, losData, camFields)}
  ${renderBankingSection(bankingVerification ?? null)}

  <footer class="footer">
    <div class="dim">Generated ${escapeHtml(generatedAtStr)}</div>
    <div class="dim" style="margin-top:4px">An evidentiary aid, not a final determination of compliance. Auditor sign-off remains required.</div>
    <div class="sign">
      <div>Auditor signature: ___________________________</div>
      <div>Date: ___________________________</div>
    </div>
  </footer>
</div>

<script>${REPORT_JS}</script>
</body>
</html>`;
}

// =========================================================================
// Audit summary table
// =========================================================================

const STATUS_META: Record<RowState, { label: string; pill: string }> = {
  matched:             { label: 'Matched',       pill: 'matched' },
  deviation_justified: { label: 'Deviation OK',  pill: 'matched' },
  mismatch:            { label: 'Mismatch',      pill: 'fail' },
  inconclusive:        { label: 'Inconclusive',  pill: 'warn' },
  skipped:             { label: 'N/A',           pill: 'na' },
};

function rowConclusion(r: DerivedRow): string {
  switch (r.state) {
    case 'matched':
      return `<span class="concl-pass">Aligned between LOS and CAM</span>`;
    case 'deviation_justified':
      return `<span class="concl-pass">Deviation approved and documented</span>`;
    case 'mismatch':
      return `<span class="concl-fail">${escapeHtml(r.finding?.title ?? 'Mismatch')}</span>`;
    case 'inconclusive':
      return `<span class="concl-warn">${escapeHtml(r.finding?.title ?? 'CAM or LOS data missing')}</span>`;
    case 'skipped':
      return `<span class="concl-na">${escapeHtml(r.skipNote ?? r.finding?.title ?? 'Not applicable')}</span>`;
  }
}

function renderSummaryTable(rows: DerivedRow[]): string {
  return `
<div class="block">
  <h2 class="section-h">Audit Summary</h2>
  <table class="summary">
    <thead>
      <tr><th class="c-pt">#</th><th>Check</th><th class="c-status">Status</th><th class="c-sev">Severity</th><th>Conclusion</th></tr>
    </thead>
    <tbody>
      ${rows.map((r, i) => {
        const meta = STATUS_META[r.state];
        const rowCls = r.state === 'mismatch' ? 'row-fail' : (i % 2 === 1 ? 'row-alt' : '');
        const sev = r.severity ?? '';
        return `
<tr class="${rowCls}">
  <td class="c-pt mono">${r.point}</td>
  <td><div class="check-name">${escapeHtml(r.label)}</div></td>
  <td class="c-status"><span class="pill ${meta.pill}">${meta.label}</span></td>
  <td class="c-sev"><span class="sev ${escapeHtml(sev)}">${escapeHtml(sev)}</span></td>
  <td>${rowConclusion(r)}</td>
</tr>`;
      }).join('')}
    </tbody>
  </table>
</div>
`;
}

// =========================================================================
// Issue detail (mismatches + inconclusives only)
// =========================================================================

function renderIssuesDetail(
  rows: DerivedRow[],
  losData: LosApplicationDataDTO | null,
  camFields: Record<string, { value: unknown }> | null,
): string {
  const mismatches = rows.filter((r) => r.state === 'mismatch');
  const inconclusives = rows.filter((r) => r.state === 'inconclusive');
  if (mismatches.length === 0 && inconclusives.length === 0) {
    return `
<div class="block">
  <h2 class="section-h">Issue Detail</h2>
  <p class="ok-msg">No mismatches. All evaluable checks aligned between LOS and CAM.</p>
</div>`;
  }
  const heading = `${mismatches.length} mismatch${mismatches.length === 1 ? '' : 'es'}${inconclusives.length > 0 ? `, ${inconclusives.length} inconclusive` : ''}`;
  return `
<div class="block">
  <h2 class="section-h">Issue Detail · ${heading}</h2>
  ${mismatches.map((r) => renderIssueCard(r, losData, camFields, false)).join('')}
  ${inconclusives.map((r) => renderIssueCard(r, losData, camFields, true)).join('')}
</div>`;
}

function renderIssueCard(
  r: DerivedRow,
  losData: LosApplicationDataDTO | null,
  camFields: Record<string, { value: unknown }> | null,
  inconclusive: boolean,
): string {
  const f = r.finding;
  const sev = f?.severity ?? '';
  const comparison = inconclusive ? '' : renderComparisonForPoint(r.point, losData, camFields);
  const pill = inconclusive
    ? `<span class="pill warn">Inconclusive</span>`
    : `<span class="pill fail">${escapeHtml(f?.title ?? 'Mismatch')}</span>`;
  return `
<div class="issue ${inconclusive ? 'amber' : ''}">
  <div class="issue-head">
    <span class="issue-point">Point ${r.point}</span>
    <span class="issue-title">${escapeHtml(r.label)}</span>
    ${pill}
    ${sev ? `<span class="sev ${escapeHtml(sev)}">${escapeHtml(sev)}</span>` : ''}
  </div>
  ${f?.description ? `<div class="issue-body">${escapeHtml(f.description)}</div>` : ''}
  ${comparison ? `<div style="padding:0 12px 10px">${comparison}</div>` : ''}
  <div class="issue-src"><strong>LOS path:</strong> ${escapeHtml(r.losPath)}${f?.ruleId ? ` · <code>${escapeHtml(f.ruleId)}</code>` : ''}</div>
</div>`;
}

function renderComparisonForPoint(
  point: number,
  losData: LosApplicationDataDTO | null,
  camFields: Record<string, { value: unknown }> | null,
): string {
  if (!losData || !camFields) return '';
  switch (point) {
    case 2: return renderCibilCompare(losData, camFields);
    case 6: return renderLoanEligibilityCompare(losData, camFields);
    case 7: return renderPolicyCompare(losData, camFields);
    case 8: return renderDeviationCompare(losData, camFields);
    case 9: return renderSanctionConditionsCompare(losData, camFields);
    default: return '';
  }
}

function verdictCell(match: boolean): string {
  return `<td>${match ? '<span class="v-ok">✓</span>' : '<span class="v-no">✗</span>'}</td>`;
}

function renderCibilCompare(losData: LosApplicationDataDTO, camFields: Record<string, { value: unknown }>): string {
  const camApps = camField<Array<{ name: string; cibil_score?: number }>>(camFields, 'applicants') ?? [];
  const losApps = losData.data.applicants ?? [];
  if (losApps.length === 0) return '';
  const rows = losApps.map((la) => {
    const ca = camApps.find((c) => (c.name ?? '').toLowerCase().trim() === (la.name ?? '').toLowerCase().trim());
    const losScore = la.cibilScore ?? null;
    const camScore = ca?.cibil_score ?? null;
    const match = losScore !== null && camScore !== null && losScore === camScore;
    return `<tr><td>${escapeHtml(la.name)}</td><td class="num">${losScore ?? '—'}</td><td class="num">${camScore ?? '—'}</td>${verdictCell(match)}</tr>`;
  }).join('');
  return `<table class="compare"><thead><tr><th>Applicant</th><th>LOS</th><th>CAM</th><th>Verdict</th></tr></thead><tbody>${rows}</tbody></table>`;
}

function renderLoanEligibilityCompare(losData: LosApplicationDataDTO, camFields: Record<string, { value: unknown }>): string {
  const ld = losData.data.loanDetails;
  if (!ld) return '';
  const checks: Array<[string, string, string]> = [
    ['Sanctioned amount', fmtInr(ld.sanctionedAmount), fmtInr(camField<number>(camFields, 'sanctioned_amount_inr'))],
    ['Interest rate', fmtPct(ld.interestRatePct), fmtPct(camField<number>(camFields, 'interest_rate_pct'))],
    ['Tenure (months)', String(ld.tenureMonths), String(camField<number>(camFields, 'tenure_months') ?? '—')],
    ['EMI', fmtInr(ld.emiAmount), fmtInr(camField<number>(camFields, 'emi_amount_inr'))],
    ['FOIR', fmtPct((ld as { foirPct?: number | null }).foirPct ?? null), fmtPct(camField<number>(camFields, 'foir_pct'))],
    ['LTV', fmtPct((ld as { ltvPct?: number | null }).ltvPct ?? null), fmtPct(camField<number>(camFields, 'ltv_pct'))],
  ];
  return `<table class="compare"><thead><tr><th>Field</th><th>LOS</th><th>CAM</th><th>Verdict</th></tr></thead><tbody>${
    checks.map(([field, los, cam]) => `<tr><td>${escapeHtml(field)}</td><td class="num">${los}</td><td class="num">${cam}</td>${verdictCell(los === cam && los !== '—')}</tr>`).join('')
  }</tbody></table>`;
}

function renderPolicyCompare(losData: LosApplicationDataDTO, camFields: Record<string, { value: unknown }>): string {
  const company = losData.data.companyCategorization;
  if (!company) return '';
  const camCat = camField<string>(camFields, 'company_category');
  const camCap = camField<number>(camFields, 'company_cap_inr');
  return `<table class="compare"><thead><tr><th>Field</th><th>LOS</th><th>CAM</th></tr></thead><tbody>
    <tr><td>Company category</td><td>${escapeHtml(company.category ?? '—')}</td><td>${escapeHtml(camCat ?? '—')}</td></tr>
    <tr><td>Max loan cap</td><td class="num">${fmtInr(company.maxLoanCapForCategory ?? null)}</td><td class="num">${fmtInr(camCap)}</td></tr>
    <tr><td>Sanctioned amount</td><td class="num">${fmtInr(losData.data.loanDetails?.sanctionedAmount ?? null)}</td><td class="dim">—</td></tr>
  </tbody></table>`;
}

function renderDeviationCompare(losData: LosApplicationDataDTO, camFields: Record<string, { value: unknown }>): string {
  const losDevs = losData.data.deviations ?? [];
  const camDevs = camField<Array<{ type?: string; mitigant?: string }>>(camFields, 'deviations') ?? [];
  const allTypes = new Set([
    ...losDevs.map((d) => d.deviationType?.toLowerCase().trim()).filter((x): x is string => !!x),
    ...camDevs.map((d) => d.type?.toLowerCase().trim()).filter((x): x is string => !!x),
  ]);
  if (allTypes.size === 0) return '<p class="dim">No deviations on file.</p>';
  const rows = [...allTypes].map((t) => {
    const inLos = losDevs.find((d) => d.deviationType?.toLowerCase().trim() === t);
    const inCam = camDevs.find((d) => d.type?.toLowerCase().trim() === t);
    return `<tr><td><code>${escapeHtml(t)}</code></td>${verdictCell(!!inLos)}${verdictCell(!!inCam)}<td>${inLos ? escapeHtml(inLos.status) : '—'}</td><td>${escapeHtml((inLos?.mitigant ?? inCam?.mitigant ?? '').slice(0, 80))}</td></tr>`;
  }).join('');
  return `<table class="compare"><thead><tr><th>Deviation type</th><th>In LOS</th><th>In CAM</th><th>Status</th><th>Mitigant</th></tr></thead><tbody>${rows}</tbody></table>`;
}

function renderSanctionConditionsCompare(losData: LosApplicationDataDTO, camFields: Record<string, { value: unknown }>): string {
  const losConditions = losData.data.sanctionConditions ?? [];
  const camConditions = camField<string[]>(camFields, 'sanction_conditions') ?? [];
  return `<div class="cond-cols">
  <div><h4>LOS conditions (${losConditions.length})</h4>${losConditions.length === 0 ? '<p class="dim">None.</p>' : '<ol>' + losConditions.map((c) => `<li>${escapeHtml(c)}</li>`).join('') + '</ol>'}</div>
  <div><h4>CAM conditions (${camConditions.length})</h4>${camConditions.length === 0 ? '<p class="dim">None.</p>' : '<ol>' + camConditions.map((c) => `<li>${escapeHtml(c)}</li>`).join('') + '</ol>'}</div>
</div>`;
}

// =========================================================================
// Banking verification (only when a run exists)
// =========================================================================

function renderBankingSection(bv: BankingVerificationDTO | null): string {
  if (!bv || bv.status !== 'completed' || !bv.response) return '';
  const r = bv.response;
  const pct = (v: number): string => `${Math.round(v * 100)}%`;
  const verdict = r.editedPdfDetected
    ? '<span class="v-no">Tamper flag raised</span>'
    : r.salaryCreditsFound === 0
      ? '<span class="v-no">No salary credits found</span>'
      : !r.salaryEmployerMatch
        ? '<span style="color:var(--amber);font-weight:700">Employer mismatch</span>'
        : '<span class="v-ok">Passed</span>';
  return `
<div class="block">
  <h2 class="section-h">Banking Verification</h2>
  <p class="dim" style="margin:0 0 8px">Independent bank statement analysis via Novel Check (provider: ${escapeHtml(bv.provider)}).</p>
  <table class="compare">
    <tbody>
      <tr><td><strong>Overall</strong></td><td>${verdict}</td></tr>
      <tr><td>Salary credits found</td><td>${r.salaryCreditsFound}</td></tr>
      <tr><td>Months covered</td><td>${r.monthsCovered}</td></tr>
      <tr><td>Monthly avg balance (verified)</td><td>${fmtInr(r.monthlyAverageBalanceInr)}</td></tr>
      <tr><td>Employer match</td><td>${r.salaryEmployerMatch ? `Yes (${pct(r.salaryEmployerMatchConfidence)})` : `No (${pct(r.salaryEmployerMatchConfidence)})`}</td></tr>
      <tr><td>PDF tamper flag</td><td>${r.editedPdfDetected ? '<span class="v-no">Editing detected</span>' : 'Clean'}</td></tr>
    </tbody>
  </table>
  ${r.anomalies.length > 0 ? `<div style="margin-top:10px"><strong>Anomaly flags</strong><ul style="margin:4px 0 0;padding-left:20px;color:#92400e">${r.anomalies.map((a: string) => `<li>${escapeHtml(a)}</li>`).join('')}</ul></div>` : ''}
</div>`;
}
